import React from 'react';
import {FlatList, Platform, View} from 'react-native';
import {useNavigation, useRoute} from '@react-navigation/native';
import {compact, get, groupBy} from 'lodash';

import ModalHeader from '/component/layout/ModalHeader';
import {useTranslation} from '/provider/TranslationProvider';
import {generateCallback} from '/util/component.util';
import {getSortOptions} from '/util/sort.util';

import {SortItem} from './SortItem';
import {styles} from './SortScreen.style';

interface SortScreenProps {
  onShowInfoPopover: () => void;
}

export function SortScreen({onShowInfoPopover = () => {}}: SortScreenProps) {
  const navigation = useNavigation();
  const route = useRoute();
  const {tt} = useTranslation('sortScreen');

  const recordsName = get(route, 'params.recordsName');
  const sortParams = get(route, 'params.sortParams');
  const setSelectedSortParam = get(route, 'params.setSelectedSortParam');
  const lastPaidRank = get(route, 'params.lastPaidRank', '');

  const sortOptions = getSortOptions({recordsName});

  const groupedOptions = groupBy(
    compact(sortOptions),
    (option: any) => option?.params?.sortBy || 'default',
  );

  // Flatten all options for global "1 of 6" indexing
  const allOptionsArray = Object.values(groupedOptions).flat() as any[];
  const totalCount = allOptionsArray.length;

  const globalIndexForKey = (key: string) =>
    allOptionsArray.findIndex(o => o.key === key);

  const sortListItem =
    (groupOptions: any[]) =>
    ({item}: any) => {
      const globalIndex = globalIndexForKey(item?.key);
      const groupIndex = groupOptions.findIndex(o => o.key === item?.key);
      const groupTotal = groupOptions.length;

      return (
        <SortItem
          item={item}
          sortParam={sortParams[recordsName]}
          setSelectedSortParam={setSelectedSortParam}
          onShowInfoPopover={onShowInfoPopover}
          lastPaidRank={lastPaidRank}
          index={Platform.OS === 'ios' ? globalIndex : groupIndex}
          total={Platform.OS === 'ios' ? totalCount : groupTotal}
        />
      );
    };

  const renderSortItem = (data: any[]) => sortListItem(data);

  return (
    <ModalHeader
      style={{
        base: styles.container,
        headerText: styles.headerText,
      }}
      title={tt('title', 'Sort by')}
      accessibilityLabel="Sort by"
      accessibilityRole="header"
      titleVariant="medium"
      titleWrapperAccessible={false}
      onClose={generateCallback({func: navigation.goBack})}
      closeIconName="chevron-left">
      <View style={styles.container}>
        {Object.entries(groupedOptions).map(([sortBy, options]) => {
          const data = options as any[];

          return (
            <View key={sortBy}>
              <View
                accessible={Platform.OS === 'android'}
                importantForAccessibility={
                  Platform.OS === 'android' ? 'yes' : 'no-hide-descendants'
                }
                accessibilityLabel={
                  Platform.OS === 'android'
                    ? `Sort group by ${sortBy}`
                    : undefined
                }>
                <FlatList
                  data={data}
                  accessible={false}
                  keyExtractor={(item: any) => item.key}
                  renderItem={renderSortItem(data)}
                />
              </View>
            </View>
          );
        })}
      </View>
    </ModalHeader>
  );
}
